import { useEffect } from "react";
import { useLocation } from "react-router-dom";
import { useCookieConsent, consumeMetaPixelPageView } from "@/hooks/useCookieConsent";
import { supabase } from "@/integrations/supabase/client";
import { updateMetaPixelUserData } from "@/lib/metaPixel";
import { ladeMetaPixelUserData } from "@/lib/metaPixelUserData";
import { nachAktivierung } from "@/lib/prerendering";

/**
 * Fires fbq('track', 'PageView') on every SPA route change,
 * but only when marketing consent has been granted and the pixel is loaded.
 *
 * For logged-in users we first hydrate the Pixel with Enhanced-Matching data
 * (em/fn/ln/ph) so the first PageView after page-load already has high
 * Match Quality (instead of waiting for the AuthContext auth-state-change).
 *
 * Mount this once in the app root (inside BrowserRouter).
 *
 * B1 (18.09.2026): loadMetaPixel() feuert direkt nach `init` bereits einen
 * PageView. Genau den überspringen wir hier einmalig (consumeMetaPixelPageView),
 * sonst zählt Meta jeden Einstieg doppelt. Die Advanced-Matching-Anreicherung
 * läuft trotzdem, damit die folgenden Events die Match-Keys haben.
 *
 * Vorab gerenderte Seiten (Speculation Rules, Paket 1d): fbevents.js kennt
 * `document.prerendering` nicht und würde den PageView für eine unsichtbare Seite
 * senden. Deshalb wartet alles hier auf die Aktivierung. `consumeMetaPixelPageView`
 * wird bewusst ERST im Callback gelesen: Bekommt loadMetaPixel denselben Guard
 * (useCookieConsent.ts, dort noch offen), setzt es den Merker ebenfalls erst bei
 * Aktivierung, und zwar vor uns, weil sein Listener zuerst registriert wurde
 * (Effekt-Reihenfolge: useCookieConsent läuft innerhalb dieses Hooks vor unserem
 * useEffect). So bleibt es in beiden Zuständen bei genau einem PageView.
 */
export function useMetaPageView() {
  const location = useLocation();
  const { hasMarketingConsent } = useCookieConsent();

  useEffect(() => {
    if (!hasMarketingConsent) return;
    if (typeof window === "undefined" || !window.fbq) return;

    const abbrechen = nachAktivierung(() => {
      const bereitsGefeuert = consumeMetaPixelPageView();

      (async () => {
        try {
          // getSession liest lokal (kein /auth/v1/user-Request wie vorher getUser); die
          // profiles-Daten kommen aus dem geteilten, je Nutzer einmaligen Loader (Perf Welle 5).
          const { data: { session } } = await supabase.auth.getSession();
          const daten = ladeMetaPixelUserData(session?.user);
          if (daten) updateMetaPixelUserData(await daten);
        } catch (err) {
          console.warn("[MetaPixel] user data enrichment failed:", err);
        }

        if (!bereitsGefeuert) window.fbq("track", "PageView");
      })();
    });

    return abbrechen;
  }, [location.pathname, hasMarketingConsent]);
}
